import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { crmApi, CRM_STATUS_STYLE } from "@/lib/crm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, XCircle, BadgeCheck, FileText } from "lucide-react";

export default function EnquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [enq, setEnq] = useState(null);
  const [meta, setMeta] = useState({ lost_reasons: [] });
  const [loading, setLoading] = useState(true);
  const [act, setAct] = useState({ type: "note", text: "", follow_up_date: "" });
  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const canEdit = can("crm", "edit");

  const load = async () => {
    setLoading(true);
    try { setEnq(await crmApi.get(id)); } catch { toast.error("Not found"); navigate("/crm"); }
    finally { setLoading(false); }
  };
  useEffect(() => { crmApi.meta().then(setMeta); load(); /* eslint-disable-next-line */ }, [id]);

  const changeStatus = async (status, reason = null) => {
    try { setEnq(await crmApi.setStatus(id, status, reason)); toast.success(`Marked ${status}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const submitLost = async () => {
    if (!lostReason) { toast.error("Select a reason"); return; }
    await changeStatus("Lost", lostReason);
    setLostOpen(false); setLostReason("");
  };

  const addActivity = async () => {
    if (!act.text) { toast.error("Enter activity details"); return; }
    try {
      setEnq(await crmApi.addActivity(id, { ...act, follow_up_date: act.follow_up_date || undefined }));
      setAct({ type: "note", text: "", follow_up_date: "" });
      toast.success("Activity logged");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (loading || !enq) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-6" data-testid="enquiry-detail-page">
      <button onClick={() => navigate("/crm")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back to enquiries</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{enq.enquiry_no}</h2>
            <Badge className={`hover:bg-inherit ${CRM_STATUS_STYLE[enq.status]}`} data-testid="enq-status-badge">{enq.status}</Badge>
          </div>
          <p className="mt-1 text-sm text-slate-500">{enq.customer?.company} · {enq.source} · created {new Date(enq.created_at).toLocaleDateString()}</p>
        </div>
        {canEdit && !["Won", "Lost"].includes(enq.status) && (
          <div className="flex flex-wrap gap-2">
            {enq.status === "New" && <Button variant="outline" onClick={() => changeStatus("Qualified")} data-testid="qualify-btn"><BadgeCheck className="mr-2 h-4 w-4" />Qualify</Button>}
            <Button variant="outline" onClick={() => navigate(`/quotations/new?enquiry=${enq.id}`)} data-testid="convert-quotation-btn"><FileText className="mr-2 h-4 w-4" />Convert to Quotation</Button>
            <Button variant="outline" className="border-emerald-300 text-emerald-700" onClick={() => changeStatus("Won")} data-testid="won-btn"><CheckCircle2 className="mr-2 h-4 w-4" />Mark Won</Button>
            <Button variant="outline" className="border-rose-300 text-rose-700" onClick={() => setLostOpen(true)} data-testid="lost-btn"><XCircle className="mr-2 h-4 w-4" />Mark Lost</Button>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-heading text-base font-semibold text-slate-900">Requirement</h3>
          <div className="mt-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase text-slate-500">Fabric</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">GSM</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Width</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Qty</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Target price</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(enq.items || []).map((it, i) => (
                  <TableRow key={i} data-testid={`detail-item-${i}`}>
                    <TableCell className="font-medium text-slate-800">{it.fabric_type}{it.design_ref ? <span className="block text-xs text-slate-400">Design {it.design_ref}</span> : null}</TableCell>
                    <TableCell className="text-slate-600">{it.gsm || "—"}</TableCell>
                    <TableCell className="text-slate-600">{it.width || "—"}</TableCell>
                    <TableCell className="text-slate-600">{it.quantity} {it.unit}</TableCell>
                    <TableCell className="text-slate-600">{it.target_price ? `₹${it.target_price}` : "—"}</TableCell>
                  </TableRow>
                ))}
                {(!enq.items || enq.items.length === 0) && <TableRow><TableCell colSpan={5} className="py-6 text-center text-slate-400">No items recorded</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          {enq.notes && <p className="mt-4 rounded-md bg-slate-50 p-3 text-sm text-slate-600"><span className="font-medium">Notes: </span>{enq.notes}</p>}
          {enq.target_delivery && <p className="mt-3 text-sm text-slate-500">Target delivery: <span className="font-medium text-slate-700">{enq.target_delivery}</span></p>}
        </Card>

        <Card className="p-6">
          <h3 className="font-heading text-base font-semibold text-slate-900">Customer</h3>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Company</dt><dd className="font-medium text-slate-800">{enq.customer?.company}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Contact</dt><dd className="text-slate-700">{enq.customer?.contact_name || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Phone</dt><dd className="text-slate-700">{enq.customer?.phone || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Email</dt><dd className="text-slate-700">{enq.customer?.email || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">City</dt><dd className="text-slate-700">{enq.customer?.city || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Owner</dt><dd className="text-slate-700">{enq.assigned_to_name}</dd></div>
          </dl>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="font-heading text-base font-semibold text-slate-900">Activity & follow-ups</h3>
        {canEdit && (
          <div className="mt-4 flex flex-wrap items-end gap-2 rounded-md bg-slate-50 p-3">
            <div className="w-36 space-y-1">
              <Label className="text-xs">Type</Label>
              <Select value={act.type} onValueChange={(v) => setAct({ ...act, type: v })}>
                <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["note", "call", "meeting", "followup"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px] space-y-1">
              <Label className="text-xs">Details</Label>
              <Input value={act.text} onChange={(e) => setAct({ ...act, text: e.target.value })} placeholder="What happened / next step" data-testid="activity-text-input" />
            </div>
            <div className="w-40 space-y-1">
              <Label className="text-xs">Follow-up date</Label>
              <Input type="date" value={act.follow_up_date} onChange={(e) => setAct({ ...act, follow_up_date: e.target.value })} />
            </div>
            <Button onClick={addActivity} data-testid="add-activity-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">Log</Button>
          </div>
        )}
        <div className="mt-4 space-y-3">
          {[...(enq.activities || [])].reverse().map((a) => (
            <div key={a.id} className="flex gap-3 border-b border-slate-100 pb-3" data-testid="activity-item">
              <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-indigo-400" />
              <div>
                <p className="text-sm text-slate-700"><span className="font-medium capitalize">{a.type}</span> · {a.text}</p>
                <p className="text-xs text-slate-400">{a.by} · {new Date(a.at).toLocaleString()}</p>
              </div>
            </div>
          ))}
          {(!enq.activities || enq.activities.length === 0) && <p className="text-sm text-slate-400">No activity yet.</p>}
        </div>
      </Card>

      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark enquiry as Lost</DialogTitle>
            <DialogDescription>Record why this enquiry was lost (used for analytics).</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Select value={lostReason} onValueChange={setLostReason}>
              <SelectTrigger data-testid="lost-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
              <SelectContent>{meta.lost_reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLostOpen(false)}>Cancel</Button>
            <Button onClick={submitLost} data-testid="confirm-lost-btn" className="bg-rose-600 text-white hover:bg-rose-700">Mark Lost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
